// https://github.com/wiley/as-api/blob/master/funder_dashboard.yaml

import ConsortiaReportTypeEnum from 'const/ConsortiaReportTypeEnum';
import { get } from 'services/ajax';

const baseUrl = `${process.env.API_URL_V2}`;
const baseUrlV3 = `${process.env.API_URL_V3}`;

/**
 * Transform API response to frontend ConsortiaStats type
 * Maps field names from API response to match frontend expectations
 */
function transformConsortiumStatsResponse(apiResponse: ConsortiumStatsApiResponse): ConsortiaStats {
  return {
    ...apiResponse,
    // Map API field names to frontend field names
    showAccountSummary: apiResponse.showSummary,
    availableBalanceOpenAccess: apiResponse.openAccessAvailableBalance,
    availableBalanceOnlineOpen: apiResponse.onlineOpenAvailableBalance,
    // Pass through capType values from API (currency is determined per-value based on capType)
    capTypeCombined: apiResponse.capTypeCombined,
    capTypeGoa: apiResponse.capTypeGoa,
    capTypeHoa: apiResponse.capTypeHoa,
    // Ensure required frontend counters are always present.
    totalOpenAccessArticleCount: apiResponse.totalOpenAccessArticleCount ?? 0,
    totalOnlineOpenArticleCount: apiResponse.totalOnlineOpenArticleCount ?? 0,
  };
}

export async function getConsortiumStats(
  consortiumId: number,
  activeState: boolean,
  fromDate?: string,
  toDate?: string,
): Promise<ConsortiaStats> {
  const params: Record<string, any> = {
    activeState,
  };

  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const apiResponse: ConsortiumStatsApiResponse = await get(`${baseUrlV3}/consortium/${consortiumId}/stats`, params);
  return transformConsortiumStatsResponse(apiResponse);
}

/**
 * V2 API: Get accounts with consortium stats included (legacy endpoint)
 * Used when consortia-available-balance feature flag is OFF
 */
export async function getAccounts(
  consortiumId: number,
  activeState: boolean,
  fromDate: string,
  toDate: string,
  sort?: string,
  page: number = 0,
  size: number = 20,
  accountName?: string,
): Promise<{
  accounts: Array<{ account: ConsortiaAccount; stats: ConsortiaStats }>;
  pagination: Pagination;
  stats: ConsortiaStats;
}> {
  return get(`${baseUrl}/deals/${consortiumId}/accounts`, {
    activeState,
    fromDate,
    toDate,
    sort,
    page,
    size,
    accountName,
  });
}

/**
 * V3 API: Get accounts without consortium stats (used with getConsortiumStats)
 * Used when consortia-available-balance feature flag is ON
 */
export async function getAccountsV3(
  consortiumId: number,
  activeState: boolean,
  fromDate: string,
  toDate: string,
  sort?: string,
  page: number = 0,
  size: number = 20,
  accountName?: string,
): Promise<{
  accounts: Array<{ account: ConsortiaAccount; stats: ConsortiaStats }>;
  pagination: Pagination;
}> {
  const params: Record<string, any> = {
    fromDate,
    toDate,
    page,
    size,
    activeState,
  };

  if (sort) params.sort = sort;
  if (accountName) params.accountName = accountName;

  return get(`${baseUrlV3}/consortium/${consortiumId}/accounts/stats`, params);
}

export async function getDealOOConfiguration(
  consortiumId: number,
  activeState: boolean,
): Promise<OrderProcessConfiguration> {
  return get(`${baseUrl}/deals/${consortiumId}/ooConfiguration`, {
    activeState,
  });
}

export async function getRequests(
  consortiumId: number,
  activeState: boolean,
  status: 'pending' | 'approved' | 'denied',
  revenueModel?: string,
  sort?: string,
  page: number = 0,
  size: number = 20,
  search?: string,
): Promise<{
  pagination: Pagination;
  requests: Array<{
    account: ConsortiaAccount;
    article: ConsortiaArticle;
    journal: { id: string; name: string; revenueModel: 'Online Open' | 'Open Access' };
    request: {
      /* modelType: string; */ id: string;
      created: string;
      amount: number;
      revenueModel: 'Online Open' | 'Open Access';
    };
  }>;
  stats: ConsortiaStats;
}> {
  return get(`${baseUrl}/deals/${consortiumId}/requests/${status}`, {
    activeState,
    revenueModel,
    sort,
    page,
    size,
    search,
  });
}

export async function createReport(
  consortiumId: number,
  activeState: boolean,
  reportName: ConsortiaReportTypeEnum,
  fromDate: string,
  toDate: string,
): Promise<void> {
  return get(`${baseUrl}/reports/${reportName}/create`, { consortiumId, activeState, fromDate, toDate });
}

export async function downloadReport(reportId: string): Promise<{ downloadUrl: string }> {
  return get(`${baseUrl}/reports/${reportId}/download`);
}
